import React, { useCallback, useEffect, useRef } from "react";

import { Enums, Types, Assets } from "@sendum/core";

import s from "./Pallet.module.scss";
import { calculateAlarmDescription, renderTime } from "../utils";
import { Button } from "@sendum/core/src/components";
import { ConfirmCompletingModal, IConfirmCompletingModalRef } from "./ConfirmCompletingModal";
import { refreshPallets, setPallet } from "../store/PalletSelectorActions";
import { AnyAction } from "redux";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { IMoveModalRef, MoveModal } from "./MoveModal";
import { PalletSelectorRootState } from "../store/PalletSelectorStore";

interface IProps {
	pallet: Types.Pallet;
	isActive?: boolean;
	isViewOnly?: boolean;
	onClick?: (pallet: Types.Pallet) => void;
	isMovement?: boolean;
}

export const Pallet = React.memo(({ pallet, isActive, isViewOnly, onClick, isMovement }: IProps): JSX.Element | null => {
	const dispatch = useDispatch();
	const account = useSelector<PalletSelectorRootState, Types.Account | undefined>((state: PalletSelectorRootState) => state.PalletSelector.account, shallowEqual);

	const palletTitleRef = useRef<HTMLDivElement>(null);
	const confirmModalRef = useRef<IConfirmCompletingModalRef>(null);
	const moveModalRef = useRef<IMoveModalRef>(null);

	const onClickCompleteHandler = useCallback(() => {
		confirmModalRef.current?.open(pallet);
	}, [pallet]);

	const onClickMoveHandler = useCallback(() => {
		moveModalRef.current?.open(pallet);
	}, [pallet]);

	const onCompleted = useCallback(() => {
		dispatch(refreshPallets(isMovement) as unknown as AnyAction);
		dispatch(setPallet(undefined) as unknown as AnyAction);
	}, [dispatch, isMovement]);

	const onMoved = useCallback(() => {
		dispatch(refreshPallets(isMovement) as unknown as AnyAction);
	}, [dispatch, isMovement]);

	useEffect(() => {
		if (palletTitleRef.current) {
			palletTitleRef.current.className = `${s.Pallet__title}`;
		}
	},[pallet, isActive]);

	if (!pallet) {
		return null;
	}

	return (
		<div
			onClick={() => onClick && onClick(pallet)}
			style={{ cursor: onClick ? "pointer" : "default" }}
			className={`${s.Pallet}
				${isActive ? s.Pallet__active : ""}
				${isViewOnly ? s.Pallet__viewOnly : ""}
				${!onClick ? s.Pallet__noActive : ""}
			`}>

			<div className={s.Pallet__column}>
				<div className={s.Pallet__row}>
					<div ref={palletTitleRef} className={s.Pallet__title}>
						{`${pallet.Batch.BatchNumber} ${!pallet.PalletNumber || pallet.PalletNumber === "No Logical Pallets" ? "" : pallet.PalletNumber}`.trim()}
					</div>
					{pallet?.CurrentState === Enums.PalletState.Alarm && (
						<img className={s.Pallet__alarmContainer} src={Assets.Alarms.AlarmTriangle} alt={""} />
					)}
					{pallet?.CurrentState === Enums.PalletState.Warning && (
						<img className={s.Pallet__alarmContainer} src={Assets.Alarms.WarningTriangle} alt={""} />
					)}
					{renderTime(s.Pallet__tor_cpd,pallet.Tor, "TOR")}
					{renderTime(s.Pallet__tor_cpd,pallet.Cpd, "CPD")}
					{(!!account && account.Role !== Types.AccountRole.VIEW_ONLY_USER) && !onClick && pallet.PalletNumber !== "No Logical Pallets" && !!pallet?.CompletedAt && (<Button title="Complete" variant="primary" className={s.Pallet__button} onClick={onClickCompleteHandler}/>)}
					{(!!account && account.Role !== Types.AccountRole.VIEW_ONLY_USER) && !onClick && pallet.PalletNumber !== "No Logical Pallets" && !pallet?.CompletedAt && !!pallet?.Location.ValidNextLocations && (<Button title="Move" variant="primary" className={s.Pallet__button} onClick={onClickMoveHandler}/>)}
				</div>
				{isActive && (
					<>
						<div className={s.Pallet__item}>
							<span>Physical Pallet Number:</span> {pallet.PalletPhysicalID ?? "--"}
						</div>
						<div className={s.Pallet__item}>
							<span>Cart(s):</span> {(pallet.Cart1 || pallet.Cart2) ? [pallet.Cart1, pallet.Cart2].filter(_ => !!_).join(",") : "--"}
						</div>
						<div className={s.Pallet__item}>
							<span>Location:</span> {pallet.Location.Label ?? pallet.Location.LocationName ?? "--"}
						</div>
						<div className={s.Pallet__item}>
							<span>Assigned Driver:</span> {pallet.AssignedDriver?.DisplayName ?? "--"}
						</div>

						{(pallet?.CurrentState === Enums.PalletState.Alarm || pallet?.CurrentState === Enums.PalletState.Warning) && (
							<div className={`${s.Pallet__item} ${s.Pallet__alert}`}>
								{calculateAlarmDescription(pallet)}
							</div>
						)}
					</>
				)}
			</div>

			<ConfirmCompletingModal ref={confirmModalRef} onCompleted={onCompleted}/>
			<MoveModal ref={moveModalRef} onMoved={onMoved} isMovement={isMovement}/>
		</div>
	);
});
